/**
 * Unified User Types for Consistent Display
 *
 * Supports both legacy role system (user.role) and RBAC system (UserRole collection)
 * with type guards to determine which format a user object uses.
 */

import { Role, UserRole as RBACUserRole } from '../../services/role.service';
import { ROLE_DISPLAY_NAMES, ROLE_BADGES } from '../../utils/roleMapping';

// ============================================================================
// Base User Types
// ============================================================================

/**
 * Profile information shared between both systems
 */
export interface UserProfile {
  title?: string;
  department?: string;
  licenseNumber?: string;
  specialties?: string[];
  phoneNumber?: string;
}

/**
 * User with legacy role field (Platform system)
 */
export interface LegacyUser {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: string; // Legacy: 'admin' | 'operator' | 'staff' | 'superadmin'
  organizationId?: string;
  organizationName?: string;
  profile?: UserProfile;
  isActive?: boolean;
  isDeleted?: boolean;
  createdAt?: string;
  updatedAt?: string;
  lastLogin?: string;
  externalId?: string;
}

/**
 * User with RBAC roles array (Settings system via users-with-roles)
 */
export interface RBACUser {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  roles: Role[]; // RBAC: Populated Role documents
  userRoles?: RBACUserRole[]; // Optional: Full UserRole junction records
  primaryRole?: Role;
  allPermissions?: string[];
  organizationId?: string;
  profile?: UserProfile;
  isActive?: boolean;
  createdAt?: string;
  updatedAt?: string;
}

/**
 * Unified user type that can be either legacy, RBAC, or both
 * This is the type that UserListItem accepts
 */
export type UserData = LegacyUser | RBACUser | (LegacyUser & RBACUser);

// ============================================================================
// Type Guards
// ============================================================================

/**
 * Check if user has legacy role field
 */
export function hasLegacyRole(user: UserData): user is LegacyUser {
  return 'role' in user && typeof (user as LegacyUser).role === 'string';
}

/**
 * Check if user has RBAC roles array
 */
export function hasRBACRoles(user: UserData): user is RBACUser {
  return 'roles' in user && Array.isArray((user as RBACUser).roles);
}

// ============================================================================
// Role Resolution (Fix for "No roles assigned" bug)
// ============================================================================

/**
 * Normalized role info for display
 */
export interface DisplayRole {
  name: string;
  displayName: string;
  color?: string;
  bg?: string;
}

/**
 * Get user's roles for display, with fallback to legacy role
 * This is the fix for the "No roles assigned" bug - it checks both systems
 *
 * Priority:
 * 1. RBAC roles array (if present and non-empty)
 * 2. Legacy role field (fallback for Platform-created users)
 * 3. Empty array (truly no roles)
 */
export function getUserDisplayRoles(user: UserData): DisplayRole[] {
  // These were local copies of the maps in utils/roleMapping, carrying only the four clinical roles.
  // A role missing from the map renders raw in the platform user list, because the table asks this
  // function. utils/roleMapping imports nothing, so there is no circular dependency to avoid by
  // keeping a local copy.

  // 1. Try RBAC roles first (new system)
  if (hasRBACRoles(user) && user.roles.length > 0) {
    return user.roles.map(role => ({
      name: role.name,
      displayName: role.displayName || ROLE_DISPLAY_NAMES[role.name] || role.name,
      color: ROLE_BADGES[role.name]?.color || 'text-gray-800',
      bg: ROLE_BADGES[role.name]?.bg || 'bg-gray-100'
    }));
  }

  // 2. Fallback to legacy role field
  if (hasLegacyRole(user) && user.role) {
    const badge = ROLE_BADGES[user.role] || { color: 'text-gray-800', bg: 'bg-gray-100' };
    return [{
      name: user.role,
      displayName: ROLE_DISPLAY_NAMES[user.role] || user.role,
      color: badge.color,
      bg: badge.bg
    }];
  }

  // 3. No roles found
  return [];
}

/**
 * Get primary role for display (first/highest priority)
 */
export function getPrimaryDisplayRole(user: UserData): DisplayRole | null {
  const roles = getUserDisplayRoles(user);
  return roles.length > 0 ? roles[0] : null;
}

// ============================================================================
// User Status
// ============================================================================

export type UserStatus = 'active' | 'inactive' | 'deleted' | 'pending';

/**
 * Get user's status for display
 */
export function getUserStatus(user: UserData): UserStatus {
  if ('isDeleted' in user && user.isDeleted) {
    return 'deleted';
  }
  if ('isActive' in user) {
    return user.isActive ? 'active' : 'inactive';
  }
  return 'active'; // Default to active if not specified
}

// ============================================================================
// Display Helpers
// ============================================================================

/**
 * Get user's full name
 */
export function getUserFullName(user: UserData): string {
  return `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email;
}

/**
 * Get user's initials for avatar
 */
export function getUserInitials(user: UserData): string {
  const first = user.firstName?.[0] || '';
  const last = user.lastName?.[0] || '';
  if (first && last) {
    return `${first}${last}`.toUpperCase();
  }
  return user.email[0].toUpperCase();
}

/**
 * Generate a consistent gradient based on user ID for avatar
 */
export function getAvatarGradient(userId: string): { from: string; to: string } {
  const gradients = [
    { from: 'from-blue-500', to: 'to-purple-600' },
    { from: 'from-green-500', to: 'to-teal-600' },
    { from: 'from-orange-500', to: 'to-red-600' },
    { from: 'from-pink-500', to: 'to-rose-600' },
    { from: 'from-indigo-500', to: 'to-blue-600' },
    { from: 'from-cyan-500', to: 'to-blue-600' },
    { from: 'from-violet-500', to: 'to-purple-600' },
    { from: 'from-amber-500', to: 'to-orange-600' },
  ];

  // Use a simple hash of user ID to pick gradient
  let hash = 0;
  for (let i = 0; i < userId.length; i++) {
    hash = ((hash << 5) - hash) + userId.charCodeAt(i);
    hash = hash & hash; // Convert to 32-bit integer
  }

  return gradients[Math.abs(hash) % gradients.length];
}
